import { useState } from 'react';
import ProgressBar from '../components/trial/ProgressBar';
import StepForm from '../components/trial/StepForm';
import PrivacyPolicyStep from '../components/trial/PrivacyPolicyStep';
import CompleteScreen from '../components/trial/CompleteScreen';
import ErrorScreen, { type TrialErrorType } from '../components/trial/ErrorScreen';
import usePostAccountTrial from '../api/usePostAccountTrial';
import type { TrialFormData } from '../types/AccountTrial';

type ScreenState = 'form' | 'complete' | 'error';
const TOTAL_STEPS = 5;

const STEP_CONFIG: { label: string; placeholder: string; type: string }[] = [
  { label: 'まずは会社名を入力してください', placeholder: '例）株式会社Lumos', type: 'text' },
  { label: '氏名を入力してください', placeholder: '例）田中一郎', type: 'text' },
  {
    label: 'メールアドレスを入力してください',
    placeholder: '例）tanakaichiro@lumos.com',
    type: 'email',
  },
  { label: '電話番号を入力してください', placeholder: '例）08012345678', type: 'tel' },
];

function validateCompanyName(value: string): string | null {
  if (!value.trim()) return '会社名を入力してください';
  if (value.trim().length < 3) return '会社名は3文字以上で入力してください';
  if (value.trim().length > 50) return '会社名は50文字以内で入力してください';
  return null;
}

function validateFullName(value: string): string | null {
  if (!value.trim()) return '氏名を入力してください';
  if (value.trim().length > 50) return '氏名は50文字以内で入力してください';
  return null;
}

function validateEmail(value: string): string | null {
  if (!value.trim()) return 'メールアドレスを入力してください';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return '正しいメールアドレスを入力してください';

  const freeEmailDomains = [
    'gmail.com',
    'yahoo.co.jp',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'outlook.jp',
    'icloud.com',
    'me.com',
    'mac.com',
    'aol.com',
    'live.com',
    'live.jp',
    'msn.com',
    'docomo.ne.jp',
    'ezweb.ne.jp',
    'softbank.ne.jp',
    'i.softbank.jp',
    'ymobile.ne.jp',
  ];

  const domain = value.trim().toLowerCase().split('@')[1];
  if (domain && freeEmailDomains.includes(domain)) {
    return '会社で使用されるメールアドレスをご入力ください。Gmail等、フリーアドレスは使用できません。';
  }

  return null;
}

function validatePhoneNumber(value: string): string | null {
  if (!value.trim()) return '電話番号を入力してください';

  // ハイフンやスペースを除去
  const digitsOnly = value.replace(/[\s-]/g, '');

  // 半角数字のみかチェック
  if (!/^\d+$/.test(digitsOnly)) {
    return '半角数字で入力してください';
  }

  // 9-11桁の範囲チェック
  if (digitsOnly.length < 9 || digitsOnly.length > 11) {
    return '半角数字 9-11桁で入力してください';
  }

  return null;
}

const VALIDATORS = [validateCompanyName, validateFullName, validateEmail, validatePhoneNumber];

export default function TrialSignupTemplate() {
  const [currentStep, setCurrentStep] = useState(1);
  const [screen, setScreen] = useState<ScreenState>('form');
  const [errorType, setErrorType] = useState<TrialErrorType>('generic');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formData, setFormData] = useState<TrialFormData>({
    companyName: '',
    fullName: '',
    email: '',
    phoneNumber: '',
    agreeToPrivacyPolicy: false,
  });

  const { mutateAsync: postTrial, isPending } = usePostAccountTrial();

  const formKeys: (keyof TrialFormData)[] = ['companyName', 'fullName', 'email', 'phoneNumber'];

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      const result = await postTrial({
        company_name: formData.companyName.trim(),
        name: formData.fullName.trim(),
        email: formData.email.trim(),
        phone_number: formData.phoneNumber.trim(),
      });

      if (result.success) {
        setScreen('complete');
      } else {
        if (result.error === 'free_email') {
          setErrorType('free_email');
        } else if (result.error === 'competitor_email') {
          setErrorType('competitor_email');
        } else {
          setErrorType('generic');
        }
        setScreen('error');
      }
    } catch {
      setErrorType('generic');
      setScreen('error');
    }
  };

  const handleBackToForm = () => {
    setScreen('form');
    setCurrentStep(3);
    setEmailError(
      '会社で使用されるメールアドレスをご入力ください。Gmail等、フリーアドレスは使用できません。'
    );
  };

  // 完了画面
  if (screen === 'complete') {
    return <CompleteScreen />;
  }

  // エラー画面
  if (screen === 'error') {
    return <ErrorScreen onBack={handleBackToForm} errorType={errorType} />;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-white">
      {/* 背景装飾（右上の青いグラデーション楕円） */}
      <div
        className="pointer-events-none absolute top-0 right-0 hidden md:block"
        style={{
          width: '796.63px',
          height: '656.65px',
          background: '#A7DCFF',
          filter: 'blur(200px)',
          opacity: 0.3,
          transform: 'translate(30%, -20%)',
        }}
      />

      {/* 背景装飾（左上の青いグラデーション楕円 - SP版のみ） */}
      <div
        className="pointer-events-none absolute top-0 right-0 block md:hidden"
        style={{
          width: '400px',
          height: '400px',
          background: '#A7DCFF',
          filter: 'blur(100px)',
          opacity: 0.3,
          transform: 'translate(30%, -30%)',
        }}
      />

      {/* ヘッダー */}
      <header
        className="relative flex h-20 items-center bg-white px-8 md:h-16 md:px-4"
        style={{ boxShadow: '0px 0px 15px rgba(2, 98, 255, 0.2)' }}
      >
        <img src="/logo/agenthub.svg" alt="エージェントハブ" className="h-8 md:h-6" />
      </header>

      {/* メインコンテンツ */}
      <div className="relative mx-auto max-w-6xl px-lg md:px-8">
        {/* タイトルセクション */}
        <div className="mt-10 mb-8 md:my-6">
          <div className="flex items-center gap-3 md:gap-5">
            {/* 完全無料バッジ */}
            <div className="relative size-[80px] shrink-0 md:size-[95px]">
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'linear-gradient(225deg, #005FFF 34%, #26ACF9 100%)',
                }}
              />
              <div
                className="absolute inset-0 flex flex-col items-center justify-center text-xl leading-tight font-bold text-white"
                style={{ letterSpacing: '2px' }}
              >
                <span>完全</span>
                <span>無料</span>
              </div>
            </div>
            <div className="flex flex-col gap-3 md:flex-1">
              <h1 className="text-heading-lg leading-[1.4] font-bold text-black md:text-heading-xxl">
                <span className="text-heading-md text-primary md:text-heading-xxl">
                  【転職エージェント向け】
                </span>
                <br />
                <span className="text-heading-md md:text-heading-xxl">
                  複数の求人媒体・採用管理システムを一元管理
                </span>
              </h1>
              <p className="hidden text-xs text-black md:mt-0 md:block md:text-body-sm md:leading-[1.7]">
                エージェントハブを
                <span className="text-sm font-semibold text-danger md:text-body-md">
                  ７日間無料
                </span>
                でお試しいただけます。トライアル終了後も料金が発生することはございません。
              </p>
            </div>
          </div>
          <p className="mt-2 block text-xs text-black md:hidden">
            エージェントハブを
            <span className="text-sm font-semibold text-danger">７日間無料</span>
            でお試しいただけます。トライアル終了後も料金が発生することはございません。
          </p>
        </div>

        {/* 2カラムレイアウト */}
        <div className="flex items-start justify-center gap-lg pb-0 max-lg:flex-col max-lg:items-center md:pb-8">
          {/* 左パネル: フォームカード */}
          <div
            className="flex w-full flex-col items-center rounded bg-white p-6 md:w-[530px] md:max-w-full md:p-[42px]"
            style={{ boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.2)' }}
          >
            {/* カードヘッダー */}
            <p className="mb-xs text-center text-[11px] leading-[150%] font-bold text-black md:mb-md md:text-base">
              {currentStep === 1
                ? '最短1分・5ステップで開始'
                : 'フォーム入力後、トライアル版がお使いいただけます'}
            </p>

            {/* プログレスバー */}
            <div className="mb-xs w-[245px] md:mb-md md:w-[385px]">
              <ProgressBar currentStep={currentStep} totalSteps={TOTAL_STEPS} />
            </div>

            {/* ステップコンテンツ */}
            <div className="w-full max-w-[386px]">
              {currentStep <= 4 ? (
                <StepForm
                  key={currentStep}
                  label={STEP_CONFIG[currentStep - 1].label}
                  placeholder={STEP_CONFIG[currentStep - 1].placeholder}
                  type={STEP_CONFIG[currentStep - 1].type}
                  value={formData[formKeys[currentStep - 1]] as string}
                  nextButtonLabel={currentStep === 1 ? '7日間無料で始める' : undefined}
                  textBelowButton={
                    currentStep === 1 ? '自動課金なし/クレジットカード登録不要' : undefined
                  }
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, [formKeys[currentStep - 1]]: value }));
                    // メールアドレスフィールドでエラーをクリア
                    if (currentStep === 3 && emailError) {
                      setEmailError(null);
                    }
                  }}
                  onNext={handleNext}
                  onBack={currentStep > 1 ? handleBack : undefined}
                  validate={VALIDATORS[currentStep - 1]}
                  externalError={currentStep === 3 ? emailError : null}
                  helperText={
                    currentStep === 3
                      ? '会社で使用されるメールアドレスをご入力ください。Gmail等、フリーアドレスは使用できません。'
                      : currentStep === 4
                        ? '半角数字 9-11桁で入力してください。例）0312345678'
                        : undefined
                  }
                />
              ) : (
                <PrivacyPolicyStep
                  agreed={formData.agreeToPrivacyPolicy}
                  onAgree={(agreed) =>
                    setFormData((prev) => ({ ...prev, agreeToPrivacyPolicy: agreed }))
                  }
                  onSubmit={handleSubmit}
                  onBack={handleBack}
                  isSubmitting={isPending}
                />
              )}
            </div>
          </div>

          {/* 右パネル: イラスト（SP版では非表示） */}
          <div className="w-[450px] md:w-[490px]">
            <img
              src="/images/trial/job-post-management.png"
              alt="全ての求人をエージェントハブが一括管理"
              className="w-full object-cover"
              width={490}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
